import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Shield, Smartphone } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useTotpInitialSetup, useTotpVerify } from '@/features/auth/hooks/useAuth'
import { useUser } from '@/app/store/auth'
import { cn } from '@/lib/utils'

const totpSetupSchema = z.object({
    code: z.string().min(6, 'Code must be at least 6 characters').max(8, 'Code must be at most 8 characters'),
})

type TotpSetupFormData = z.infer<typeof totpSetupSchema>

export function TotpSetupPage() {
    const [setupData, setSetupData] = useState<any>(null)
    const [step, setStep] = useState<'setup' | 'verify'>('setup')

    const user = useUser()
    const totpInitialSetup = useTotpInitialSetup()
    const totpVerify = useTotpVerify()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<TotpSetupFormData>({
        resolver: zodResolver(totpSetupSchema),
    })

    useEffect(() => {
        // Start TOTP setup when component mounts (only once)
        if (step === 'setup' && !setupData && user?.email && !totpInitialSetup.isPending && !totpInitialSetup.data && !totpInitialSetup.error) {
            totpInitialSetup.mutate(user.email)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Only run on mount - don't retry on errors or state changes

    useEffect(() => {
        if (totpInitialSetup.data) {
            setSetupData(totpInitialSetup.data)
        }
    }, [totpInitialSetup.data])

    const onSubmit = (data: TotpSetupFormData) => {
        // The secret is not stored on the server until this first code proves it was scanned, so
        // the ticket that came with the QR code goes back with the code.
        totpVerify.mutate({ token: data.code, setupTicket: setupData?.setupTicket })
    }


    // Show loading state while fetching setup data
    if (step === 'setup' && (!setupData || totpInitialSetup.isPending)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md space-y-6">
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <Logo className="h-16" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-2xl font-bold">Setting Up Authenticator</h1>
                            <p className="text-muted-foreground">
                                Please wait while we prepare your setup...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (step === 'setup' && setupData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md space-y-6">
                    {/* Header */}
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <Logo className="h-16" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-2xl font-bold">Set Up Authenticator App</h1>
                            <p className="text-muted-foreground">
                                Scan the QR code with your authenticator app
                            </p>
                        </div>
                    </div>

                    {/* Setup form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Step 1: Add Account</CardTitle>
                            <CardDescription>
                                Use your authenticator app to scan this QR code
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* QR Code */}
                            <div className="flex justify-center">
                                {setupData.qrCode && (
                                    <img
                                        src={setupData.qrCode}
                                        alt="TOTP QR Code"
                                        className="w-48 h-48 border rounded-lg"
                                    />
                                )}
                            </div>


                            {/* Instructions */}
                            <Alert>
                                <Smartphone className="h-4 w-4" />
                                <AlertDescription>
                                    <strong>Scan the QR code with your authenticator app:</strong>
                                    <br />
                                    • Google Authenticator
                                    <br />
                                    • Microsoft Authenticator
                                    <br />
                                    • Authy
                                    <br />
                                    • 1Password
                                </AlertDescription>
                            </Alert>

                            <Button
                                type="button"
                                className="w-full"
                                onClick={() => setStep('verify')}
                            >
                                I've Added the Account
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        )
    }

    if (step === 'verify') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md space-y-6">
                    {/* Header */}
                    <div className="text-center space-y-2">
                        <div className="flex justify-center">
                            <Shield className="h-8 w-8 text-primary" />
                        </div>
                        <h1 className="text-2xl font-bold">Verify Setup</h1>
                        <p className="text-muted-foreground">
                            Enter the 6-digit code from your authenticator app
                        </p>
                    </div>

                    {/* Verification form */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Step 2: Verify</CardTitle>
                            <CardDescription>
                                Enter the code shown in your authenticator app
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="code">Authentication Code</Label>
                                    <Input
                                        id="code"
                                        type="text"
                                        placeholder="Enter 6-digit code"
                                        maxLength={8}
                                        {...register('code')}
                                        className={cn(errors.code && 'border-destructive')}
                                    />
                                    {errors.code && (
                                        <p className="text-sm text-destructive">{errors.code.message}</p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={totpVerify.isPending}
                                >
                                    {totpVerify.isPending ? 'Verifying...' : 'Verify & Complete Setup'}
                                </Button>
                            </form>

                            <div className="text-center">
                                <Button
                                    variant="link"
                                    className="text-sm"
                                    onClick={() => setStep('setup')}
                                >
                                    Back to QR Code
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        )
    }

    // Fallback loading state
    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <Logo className="h-10" />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold">Setting Up Authenticator</h1>
                        <p className="text-muted-foreground">
                            Please wait while we prepare your setup...
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
